import { HttpClient } from '@angular/common/http';
import {
    afterNextRender,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    OnDestroy,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { CoverTemplateEntity } from 'src/app/domain/entities/partials/cover-template/cover-template.entity';
import { environment } from 'src/environments/environment';
import {
    applySvgColorModifications,
    coverBackgroundPositionCss,
    coverBackgroundSizeCss,
    renderCoverItemsHtml,
} from './cover-renderer';

/** Largura da folha desenhada: A4 a 96dpi, a mesma do PDF. */
const SHEET_WIDTH_PX = 794;

@Component({
    selector: 'cover',
    templateUrl: 'cover.component.html',
    styleUrls: ['cover.component.scss', '../../document-preview.scss'],
    standalone: false,
})
export class CoverComponent implements OnDestroy {
    public template = input.required<CoverTemplateEntity>();
    public isLoading = signal<boolean>(true);
    public itemsCoverHtml = signal<SafeHtml[]>([]);
    public imageBackground = signal<string>('');

    /**
     * Fator que encolhe a folha de 794px para a largura do palco. `zoom`, e não `transform`:
     * ele escala o layout de verdade, então o palco fica com a altura da folha encolhida e
     * nada sobra para rolar (mesma escolha do `buildPage` e do composer da prévia).
     */
    public zoom = signal<number>(1);

    private _stage = viewChild<ElementRef<HTMLElement>>('stage');
    private _stageObserver?: ResizeObserver;

    /** Chave (value+colors) do último fundo carregado — evita refetch do SVG à toa. */
    private _lastBgKey: string | null = null;

    public backgroundSize = computed(() => coverBackgroundSizeCss(this.template()?.background?.boxFit));

    public backgroundPosition = computed(() =>
        coverBackgroundPositionCss(this.template()?.background?.imageAlignment)
    );

    private _sanitizer = inject(DomSanitizer);
    private _translate = inject(TranslateService);

    // * [Injects]
    private _httpClient = inject(HttpClient);

    constructor() {
        // Reconstrói os itens sempre que o template muda (preview ao vivo no editor).
        effect(() => {
            this.template();
            untracked(() => this._loadItemsCoverHtml());
        });

        // Recarrega o SVG de fundo só quando `value`/`colors` mudam (fetch é caro).
        effect(() => {
            const bg = this.template()?.background;
            const key = JSON.stringify({ v: bg?.value ?? '', c: bg?.colors ?? [] });
            if (key === this._lastBgKey) return;
            this._lastBgKey = key;
            untracked(() => this._loadBackgroundImage());
        });

        // O palco existe desde o primeiro render (o loading fica dentro dele), então dá
        // para começar a medir antes de a folha aparecer: quando ela entra, o zoom já está.
        afterNextRender(() => this._fitSheetToStage());
    }

    ngOnDestroy(): void {
        this._stageObserver?.disconnect();

        // Limpa o blob URL para evitar vazamentos de memória
        const url = this.imageBackground();
        if (url && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
        }
    }

    /** Mede o palco e segue medindo: o card da prévia muda de largura com a janela. */
    private _fitSheetToStage(): void {
        const stage = this._stage()?.nativeElement;
        if (!stage) return;

        const fit = (width: number) => {
            if (width > 0) this.zoom.set(Math.round((width / SHEET_WIDTH_PX) * 10000) / 10000);
        };

        if (typeof ResizeObserver === 'undefined') {
            fit(stage.clientWidth);
            return;
        }

        this._stageObserver = new ResizeObserver(entries => {
            fit(entries[0]?.contentRect.width ?? stage.clientWidth);
        });
        this._stageObserver.observe(stage);
    }

    /**
     * Renderiza os itens da capa via cover-renderer (fonte única do layout,
     * compartilhada com o render server-side do PDF).
     *
     * `pdfConfigs`, e não `webConfigs`: a folha aqui é a folha do PDF, só encolhida. Com os
     * valores da web (pensados para um card de altura livre) o conteúdo deixava de bater com
     * o fundo, que é desenhado nas medidas do PDF.
     */
    private _loadItemsCoverHtml(): void {
        const blocks = renderCoverItemsHtml(this.template(), {
            platform: 'pdfConfigs',
            translate: tag => this._translate.instant(tag),
        });

        this.itemsCoverHtml.set(blocks.map(html => this._sanitizer.bypassSecurityTrustHtml(html)));
        this.isLoading.set(false);
    }

    /**
     * Converte a URL do CDN para usar o proxy em desenvolvimento local
     *
     * Comportamento por ambiente:
     * - Localhost: Usa proxy (/covers/...) para evitar CORS
     * - Dev/Hom/Prod: Usa URL completa do CDN diretamente
     *
     * Exemplo: https://fover-docs-cdn-front.nyc3.cdn.digitaloceanspaces.com/covers/... -> /covers/...
     */
    private _getProxyUrl(url: string): string {
        // Usa proxy apenas em desenvolvimento local (localhost)
        // Em dev/hom/prod, o proxy não existe, então usa a URL completa do CDN
        const isLocalDev = environment.API_BASE.includes('localhost');

        // Aceita ambas as variações da URL do CDN (com e sem .cdn)
        const cdnUrlVariations = [
            'https://fover-docs-cdn-front.nyc3.cdn.digitaloceanspaces.com',
            'https://fover-docs-cdn-front.nyc3.digitaloceanspaces.com',
        ];

        if (isLocalDev) {
            for (const cdnUrl of cdnUrlVariations) {
                if (url.startsWith(cdnUrl)) {
                    // Extrai o caminho após o domínio para usar o proxy do Angular
                    const path = url.replace(cdnUrl, '');
                    // Garante que o path comece com / para funcionar com o proxy
                    const proxyPath = path.startsWith('/') ? path : `/${path}`;

                    return proxyPath;
                }
            }
        }
        // Em dev/hom/prod, retorna a URL original do CDN
        return url;
    }

    /**
     * Força a conversão da URL do CDN para usar o proxy (fallback)
     */
    private _forceProxyConversion(url: string): string {
        const cdnUrlVariations = [
            'https://fover-docs-cdn-front.nyc3.cdn.digitaloceanspaces.com',
            'https://fover-docs-cdn-front.nyc3.digitaloceanspaces.com',
        ];

        for (const cdnUrl of cdnUrlVariations) {
            if (url.includes(cdnUrl)) {
                const path = url.replace(cdnUrl, '');
                return path.startsWith('/') ? path : `/${path}`;
            }
        }

        // Se não conseguir converter, tenta extrair o path de qualquer forma
        try {
            const urlObj = new URL(url);
            return urlObj.pathname;
        } catch {
            return url;
        }
    }

    /**
     * Carrega a imagem de fundo como blob e aplica modificações de cor
     *
     * Estratégia por ambiente:
     * - Localhost: Usa HttpClient com proxy (proxy só funciona com HttpClient)
     * - Dev/Hom/Prod: Usa fetch nativo para evitar headers automáticos do HttpClient
     *   que causam preflight requests. Fetch não adiciona headers customizados,
     *   então funciona com Access-Control-Allow-Origin: *
     */
    private _loadBackgroundImage(): void {
        const originalUrl = this.template().background?.value ?? '';

        if (!originalUrl) {
            this.isLoading.set(false);
            return;
        }

        const isLocalDev =
            environment.API_BASE.includes('localhost') || window.location.hostname === 'localhost';
        const proxyUrl = this._getProxyUrl(originalUrl);

        // Se estiver em localhost e a URL ainda for absoluta (não foi convertida para proxy),
        // força a conversão manualmente
        const finalUrl =
            isLocalDev && proxyUrl.startsWith('http')
                ? this._forceProxyConversion(originalUrl)
                : proxyUrl;

        if (isLocalDev && finalUrl.startsWith('/')) {
            // Localhost: usa HttpClient com proxy (carrega como blob e converte para texto)
            // IMPORTANTE: A URL deve ser relativa (começar com /) para o proxy funcionar
            const imageDownloaded = this._httpClient.get(finalUrl, {
                responseType: 'blob',
            });

            imageDownloaded.subscribe({
                next: async (blob: Blob) => {
                    try {
                        // Converte blob para texto para poder modificar o SVG
                        const svgString = await blob.text();
                        this._processSvgAndSetBackground(svgString, originalUrl);
                    } catch (error) {
                        console.error('Erro ao converter blob para texto:', error);
                        this._loadImageWithFetch(originalUrl);
                    }
                },
                error: error => {
                    console.error('Erro ao carregar imagem:', error);
                    this._loadImageWithFetch(originalUrl);
                },
            });
        } else if (isLocalDev) {
            // Se estiver em localhost mas a URL não foi convertida, tenta usar fetch como fallback
            console.warn('⚠️ URL não foi convertida para proxy, tentando fetch:', finalUrl);
            this._loadImageWithFetch(originalUrl);
        } else {
            // Dev/Hom/Prod: usa fetch nativo para evitar preflight
            this._loadImageWithFetch(originalUrl);
        }
    }

    /**
     * Carrega a imagem usando fetch nativo (sem headers customizados)
     * Tenta múltiplas estratégias se o CORS estiver bloqueando
     */
    private async _loadImageWithFetch(url: string): Promise<void> {
        // Estratégia 1: Tenta fetch nativo (requisição simples)
        try {
            const response = await fetch(url, {
                method: 'GET',
                mode: 'cors', // Força modo CORS
                // Não adiciona headers customizados para garantir requisição simples
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const svgString = await response.text();
            this._processSvgAndSetBackground(svgString, url);
            return;
        } catch (fetchError: any) {
            console.error('❌ Fetch falhou:', {
                message: fetchError.message,
                name: fetchError.name,
                stack: fetchError.stack,
            });
            console.warn('🔄 Tentando HttpClient como fallback...');
        }

        // Estratégia 2: Tenta HttpClient (interceptor já está configurado para não adicionar Authorization)
        try {
            const imageDownloaded = this._httpClient.get(url, {
                responseType: 'text',
            });

            await new Promise<void>((resolve, reject) => {
                imageDownloaded.subscribe({
                    next: (svgString: string) => {
                        this._processSvgAndSetBackground(svgString, url);
                        resolve();
                    },
                    error: error => {
                        reject(error);
                    },
                });
            });
            return;
        } catch (httpError) {
            console.warn('HttpClient também falhou:', httpError);
        }

        // Estratégia 3: Fallback final - usa a URL diretamente
        // O navegador pode conseguir carregar como background-image mesmo com CORS bloqueado
        console.warn('Todas as estratégias falharam, usando URL diretamente como fallback');
        // Revoga o blob URL anterior antes de trocar (mesmo padrão de _processSvgAndSetBackground)
        const oldUrl = this.imageBackground();
        if (oldUrl && oldUrl.startsWith('blob:')) {
            URL.revokeObjectURL(oldUrl);
        }
        this.imageBackground.set(url);
    }

    /**
     * Processa o SVG aplicando modificações de cor e cria o blob URL
     */
    private _processSvgAndSetBackground(svgString: string, fallbackUrl: string): void {
        if (!svgString) {
            console.warn('SVG vazio, usando URL original como fallback');
            this.imageBackground.set(fallbackUrl);
            return;
        }

        try {
            // Revoga o blob URL anterior se existir
            const oldUrl = this.imageBackground();
            if (oldUrl && oldUrl.startsWith('blob:')) {
                URL.revokeObjectURL(oldUrl);
            }

            // Aplica as modificações de cor no SVG
            const modifiedSvg = applySvgColorModifications(
                svgString,
                this.template().background?.colors
            );

            // Cria um Blob URL para o SVG modificado
            const blob = new Blob([modifiedSvg], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blob);
            this.imageBackground.set(url);
        } catch (error) {
            console.error('Erro ao processar SVG:', error);
            // Em caso de erro, usa a URL original como fallback
            this.imageBackground.set(fallbackUrl);
        }
    }
}
